/**
 * Capa de red del panel.
 *
 * Un unico modulo concentra las peticiones al backend: cabeceras de
 * autenticacion, organizacion activa, traduccion de errores a `ApiError` y
 * tratamiento del 401 (sesion caducada). Los componentes nunca llaman a
 * `fetch` directamente.
 */
import type {
  AccountDiagnostic,
  AccountWebhookInfo,
  AiSettingsView,
  ApiKeyView,
  ContactFieldView,
  ContactListQuery,
  ContactView,
  CreateContactFieldRequest,
  CreateContactRequest,
  ConversationDetail,
  ConversationListQuery,
  ConversationSummary,
  CreateAccountRequest,
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  CreateKnowledgeDocumentRequest,
  CreateTemplateRequest,
  CreateTenantRequest,
  CreateTenantResponse,
  HealthResponse,
  KnowledgeDocumentView,
  LoginRequest,
  LoginResponse,
  MeResponse,
  MessageView,
  MetricsResponse,
  Paginated,
  SendMessageError,
  SendMessageRequest,
  SendMessageResponse,
  TeamMemberView,
  TemplateView,
  TenantView,
  UpdateAccountRequest,
  UpdateAiSettingsRequest,
  UpdateContactFieldRequest,
  UpdateContactRequest,
  UpdateTenantRequest,
  WhatsappAccountView,
} from '@shared/api-types.ts';
import type { BotMode, ConversationStatus, MemberRole } from '@domain/types.ts';

/** Cuerpo de error que devuelve la API (`SendMessageError` en el caso del envio). */
type ApiErrorBody = {
  error?: unknown;
  code?: unknown;
  message?: unknown;
  requiresTemplate?: unknown;
  details?: unknown;
};

export class ApiError extends Error {
  /** Codigo HTTP; `0` cuando no hubo respuesta (red caida, API apagada). */
  readonly status: number;
  readonly code: string | null;
  /** El backend pide ofrecer el formulario de plantilla (ventana de 24 h cerrada). */
  readonly requiresTemplate: boolean;
  /**
   * Detalle del fallo de validacion cuando la API lo manda: `{ campo: [mensajes] }`.
   * Un formulario puede asi senalar el campo concreto en vez de un error generico.
   */
  readonly invalidFields: Record<string, string[]> | null;

  constructor(
    status: number,
    message: string,
    code: string | null,
    requiresTemplate: boolean,
    invalidFields: Record<string, string[]> | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requiresTemplate = requiresTemplate;
    this.invalidFields = invalidFields;
  }
}

export type ApiAuth = { token: string; tenantId: string };

let currentAuth: ApiAuth | null = null;
let unauthorizedHandler: (() => void) | null = null;

/** Fija el token y la organizacion activa que acompanan a todas las peticiones. */
export function setApiAuth(auth: ApiAuth | null): void {
  currentAuth = auth;
}

export function getApiAuth(): ApiAuth | null {
  return currentAuth;
}

/** El 401 se avisa una sola vez al propietario de la sesion (App) para desloguear. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/** Mensaje listo para mostrar al agente, venga de donde venga el fallo. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return 'Ha ocurrido un error inesperado.';
}

export function unauthorizedMessage(): string {
  return 'La sesion ha caducado. Vuelve a iniciar sesion.';
}

// --- Utilidades internas -----------------------------------------------------

type QueryValue = string | number | boolean | null | undefined;

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const search = params.toString();
  return search ? `${path}?${search}` : path;
}

function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

/**
 * El `details` del backend puede venir como `parsed.error.flatten()` de zod
 * (`fieldErrors` / `formErrors`), como un mapa de campo a lista de mensajes o
 * como un mapa directo de campo a un unico mensaje (`invalid_attributes` e
 * `invalid_field` de los contactos usan esta ultima forma). Aqui se normalizan
 * las tres quedandose solo con los mensajes de texto.
 */
function readInvalidFields(details: unknown): Record<string, string[]> | null {
  if (!details || typeof details !== 'object') return null;

  const fields: Record<string, string[]> = {};
  const add = (key: string, value: unknown): void => {
    if (typeof value === 'string') {
      const message = value.trim();
      if (message.length > 0) fields[key] = [message];
      return;
    }
    if (Array.isArray(value)) {
      const messages = value.filter((item): item is string => typeof item === 'string');
      if (messages.length > 0) fields[key] = messages;
    }
  };

  const source = details as { fieldErrors?: unknown; formErrors?: unknown; [key: string]: unknown };
  if (source.fieldErrors && typeof source.fieldErrors === 'object') {
    for (const [key, value] of Object.entries(source.fieldErrors)) add(key, value);
  } else {
    for (const [key, value] of Object.entries(source)) add(key, value);
  }
  add('_form', source.formErrors);

  return Object.keys(fields).length > 0 ? fields : null;
}

function toApiError(status: number, payload: unknown): ApiError {
  const body: ApiErrorBody =
    payload && typeof payload === 'object' ? (payload as ApiErrorBody) : {};
  const message =
    typeof body.message === 'string' && body.message.length > 0
      ? body.message
      : typeof body.error === 'string' && body.error.length > 0
        ? body.error
        : `La peticion ha fallado (HTTP ${status}).`;
  const code = typeof body.code === 'string' ? body.code : null;
  return new ApiError(
    status,
    message,
    code,
    body.requiresTemplate === true,
    readInvalidFields(body.details),
  );
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
  /**
   * Las rutas de plataforma (`/api/tenants`) no operan dentro de una
   * organizacion, asi que no deben llevar `X-Tenant-Id`.
   */
  omitTenantHeader?: boolean;
};

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (currentAuth) {
    headers.Authorization = `Bearer ${currentAuth.token}`;
    if (!options.omitTenantHeader) headers['X-Tenant-Id'] = currentAuth.tenantId;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (error) {
    if (isAbortError(error)) throw error;
    throw new ApiError(
      0,
      'No se pudo conectar con el servidor. Comprueba que la API esta en marcha en http://localhost:3000.',
      'network_error',
      false,
    );
  }

  if (response.status === 401) {
    unauthorizedHandler?.();
    throw new ApiError(401, unauthorizedMessage(), 'unauthorized', false);
  }

  const raw = await response.text();
  const payload: unknown = raw.length > 0 ? parseJson(raw) : null;

  if (!response.ok) throw toApiError(response.status, payload);

  // Respuestas sin cuerpo (204) o con un JSON que el contrato ya garantiza.
  // La comprobacion de forma se hace al normalizar cada coleccion mas abajo.
  return payload as T;
}

/**
 * La API puede devolver una coleccion como array plano o envuelta en
 * `{ items: [...] }`. Se aceptan las dos formas (comprobadas en tiempo de
 * ejecucion) para que un cambio de envoltorio no rompa la interfaz.
 */
function asList<T>(payload: unknown, key = 'items'): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === 'object') {
    const value = (payload as Record<string, unknown>)[key];
    if (Array.isArray(value)) return value as T[];
  }
  return [];
}

function asPage<T>(payload: unknown): Paginated<T> {
  if (payload && typeof payload === 'object') {
    const page = payload as { items?: unknown; nextCursor?: unknown; total?: unknown };
    if (Array.isArray(page.items)) {
      return {
        items: page.items as T[],
        nextCursor: typeof page.nextCursor === 'string' ? page.nextCursor : null,
        total: typeof page.total === 'number' ? page.total : undefined,
      };
    }
  }
  return { items: asList<T>(payload), nextCursor: null };
}

// --- Contrato -----------------------------------------------------------------

export type UpdateConversationRequest = {
  status?: ConversationStatus;
  /** `null` deja la conversacion sin asignar. */
  assigneeUserId?: string | null;
  botMode?: BotMode;
  tags?: string[];
};

export type MessagePageQuery = { limit?: number; cursor?: string | null };

/**
 * Alta de una persona en una organizacion concreta.
 * El backend no publica un tipo con nombre para este cuerpo en el contrato
 * compartido, asi que se declara aqui tal y como lo valida `POST /api/tenants/:id/members`.
 */
export type AddTenantMemberRequest = {
  email: string;
  name: string;
  password: string;
  role: MemberRole;
};

export type AddTenantMemberResponse = { ok: true; userId: string };

/** Filtros de `GET /api/templates`. */
export type TemplateListQuery = {
  accountId?: string;
  status?: TemplateView['status'];
  /** Fuerza una sincronizacion con Meta antes de responder. */
  sync?: boolean;
};

/** Respuesta de `POST /api/templates/sync`: cuantas plantillas se procesaron. */
export type TemplateSyncResponse = { ok: true; templates: number };

export const api = {
  login(body: LoginRequest): Promise<LoginResponse> {
    return request<LoginResponse>('/api/auth/login', { method: 'POST', body });
  },

  me(signal?: AbortSignal): Promise<MeResponse> {
    return request<MeResponse>('/api/auth/me', { signal });
  },

  async listConversations(
    query: ConversationListQuery,
    signal?: AbortSignal,
  ): Promise<Paginated<ConversationSummary>> {
    const payload = await request<unknown>('/api/conversations', {
      query: {
        status: query.status,
        assigneeUserId: query.assigneeUserId,
        accountId: query.accountId,
        q: query.q,
        limit: query.limit,
        cursor: query.cursor,
      },
      signal,
    });
    return asPage<ConversationSummary>(payload);
  },

  getConversation(id: string, signal?: AbortSignal): Promise<ConversationDetail> {
    return request<ConversationDetail>(`/api/conversations/${encodeURIComponent(id)}`, { signal });
  },

  async listMessages(
    conversationId: string,
    query: MessagePageQuery = {},
    signal?: AbortSignal,
  ): Promise<Paginated<MessageView>> {
    const payload = await request<unknown>(
      `/api/conversations/${encodeURIComponent(conversationId)}/messages`,
      { query: { limit: query.limit, cursor: query.cursor ?? undefined }, signal },
    );
    return asPage<MessageView>(payload);
  },

  updateConversation(
    id: string,
    body: UpdateConversationRequest,
    signal?: AbortSignal,
  ): Promise<ConversationDetail> {
    return request<ConversationDetail>(`/api/conversations/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
      signal,
    });
  },

  sendMessage(
    conversationId: string,
    body: SendMessageRequest,
    signal?: AbortSignal,
  ): Promise<SendMessageResponse> {
    return request<SendMessageResponse>(
      `/api/conversations/${encodeURIComponent(conversationId)}/messages`,
      { method: 'POST', body, signal },
    );
  },

  markRead(conversationId: string, signal?: AbortSignal): Promise<void> {
    return request<void>(`/api/conversations/${encodeURIComponent(conversationId)}/read`, {
      method: 'POST',
      signal,
    });
  },

  // --- Contactos -------------------------------------------------------------

  /**
   * Lista paginada de contactos.
   *
   * `field` filtra por un campo personalizado con el formato `clave:valor`; el
   * backend compara el valor como texto, aunque el campo sea numerico.
   */
  async listContacts(
    query: ContactListQuery = {},
    signal?: AbortSignal,
  ): Promise<Paginated<ContactView>> {
    const payload = await request<unknown>('/api/contacts', {
      query: {
        q: query.q,
        optInStatus: query.optInStatus,
        field: query.field,
        limit: query.limit,
        cursor: query.cursor,
      },
      signal,
    });
    return asPage<ContactView>(payload);
  },

  getContact(id: string, signal?: AbortSignal): Promise<ContactView> {
    return request<ContactView>(`/api/contacts/${encodeURIComponent(id)}`, { signal });
  },

  /**
   * Alta manual de un contacto (HTTP 201).
   *
   * La API responde 409 `duplicate_contact` si ya existe ese numero y 400
   * `invalid_body` / `invalid_attributes` con el detalle en `details` (ver
   * `ApiError.invalidFields`).
   */
  createContact(body: CreateContactRequest, signal?: AbortSignal): Promise<ContactView> {
    return request<ContactView>('/api/contacts', { method: 'POST', body, signal });
  },

  /**
   * Cambia un contacto ya existente. Un campo **ausente** no toca el valor
   * guardado, una **cadena** lo reemplaza y **`null`** lo borra; los valores de
   * `attributes` se guardan con su tipo (numero o booleano, no texto).
   */
  updateContact(
    id: string,
    body: UpdateContactRequest,
    signal?: AbortSignal,
  ): Promise<ContactView> {
    return request<ContactView>(`/api/contacts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
      signal,
    });
  },

  /** Definiciones de los campos personalizados, en orden de formulario. */
  async listContactFields(signal?: AbortSignal): Promise<ContactFieldView[]> {
    return asList<ContactFieldView>(await request<unknown>('/api/contacts/fields', { signal }));
  },

  /** `key` es opcional: si no se envia, el backend la deriva del nombre. */
  createContactField(
    body: CreateContactFieldRequest,
    signal?: AbortSignal,
  ): Promise<ContactFieldView> {
    return request<ContactFieldView>('/api/contacts/fields', { method: 'POST', body, signal });
  },

  updateContactField(
    id: string,
    body: UpdateContactFieldRequest,
    signal?: AbortSignal,
  ): Promise<ContactFieldView> {
    return request<ContactFieldView>(`/api/contacts/fields/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
      signal,
    });
  },

  /** Borra la definicion; los valores ya guardados en los contactos se conservan. */
  deleteContactField(id: string, signal?: AbortSignal): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>(`/api/contacts/fields/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      signal,
    });
  },

  async listAccounts(signal?: AbortSignal): Promise<WhatsappAccountView[]> {
    return asList<WhatsappAccountView>(await request<unknown>('/api/accounts', { signal }));
  },

  /**
   * Da de alta un numero de WhatsApp Business (HTTP 201).
   *
   * La API responde 409 `duplicate_number` si el `phoneNumberId` ya existe y 400
   * `invalid_body` con el detalle de zod en `details` (ver `ApiError.invalidFields`).
   */
  createAccount(
    body: CreateAccountRequest,
    signal?: AbortSignal,
  ): Promise<WhatsappAccountView> {
    return request<WhatsappAccountView>('/api/accounts', { method: 'POST', body, signal });
  },

  /**
   * Cambia un numero ya dado de alta.
   *
   * El cuerpo respeta las tres convenciones del contrato: un campo **ausente**
   * no toca el valor guardado, una **cadena** lo reemplaza y **`null`** lo borra
   * (solo `appSecret` y `verifyToken`). `accessToken` no admite `null`: un numero
   * sin token no puede enviar nada.
   *
   * La API responde 404 `not_found` si el numero no existe y 400 `invalid_body`
   * con el detalle de zod en `details` (ver `ApiError.invalidFields`).
   */
  updateAccount(
    id: string,
    body: UpdateAccountRequest,
    signal?: AbortSignal,
  ): Promise<WhatsappAccountView> {
    return request<WhatsappAccountView>(`/api/accounts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
      signal,
    });
  },

  /**
   * Diagnostico de la conexion del numero contra Meta.
   * Puede tardar unos segundos: el backend consulta la Graph API.
   */
  verifyAccount(id: string, signal?: AbortSignal): Promise<AccountDiagnostic> {
    return request<AccountDiagnostic>(`/api/accounts/${encodeURIComponent(id)}/verify`, {
      method: 'POST',
      signal,
    });
  },

  /** URL y token que hay que pegar en Meta para configurar el webhook. */
  getAccountWebhookInfo(id: string, signal?: AbortSignal): Promise<AccountWebhookInfo> {
    return request<AccountWebhookInfo>(`/api/accounts/${encodeURIComponent(id)}/webhook-info`, {
      signal,
    });
  },

  async listTeam(signal?: AbortSignal): Promise<TeamMemberView[]> {
    return asList<TeamMemberView>(await request<unknown>('/api/team', { signal }));
  },

  // --- Organizaciones (solo personal de la plataforma) -----------------------

  async listTenants(signal?: AbortSignal): Promise<TenantView[]> {
    return asList<TenantView>(
      await request<unknown>('/api/tenants', { signal, omitTenantHeader: true }),
    );
  },

  /** Zonas horarias IANA sugeridas para el desplegable del formulario. */
  async listTenantTimezones(signal?: AbortSignal): Promise<string[]> {
    return asList<string>(
      await request<unknown>('/api/tenants/timezones', { signal, omitTenantHeader: true }),
    );
  },

  createTenant(body: CreateTenantRequest, signal?: AbortSignal): Promise<CreateTenantResponse> {
    return request<CreateTenantResponse>('/api/tenants', {
      method: 'POST',
      body,
      signal,
      omitTenantHeader: true,
    });
  },

  updateTenant(
    id: string,
    body: UpdateTenantRequest,
    signal?: AbortSignal,
  ): Promise<TenantView> {
    return request<TenantView>(`/api/tenants/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
      signal,
      omitTenantHeader: true,
    });
  },

  addTenantMember(
    id: string,
    body: AddTenantMemberRequest,
    signal?: AbortSignal,
  ): Promise<AddTenantMemberResponse> {
    return request<AddTenantMemberResponse>(
      `/api/tenants/${encodeURIComponent(id)}/members`,
      { method: 'POST', body, signal, omitTenantHeader: true },
    );
  },

  getMetrics(signal?: AbortSignal): Promise<MetricsResponse> {
    return request<MetricsResponse>('/api/metrics', { signal });
  },

  getAiSettings(signal?: AbortSignal): Promise<AiSettingsView> {
    return request<AiSettingsView>('/api/settings/ai', { signal });
  },

  updateAiSettings(body: UpdateAiSettingsRequest, signal?: AbortSignal): Promise<AiSettingsView> {
    return request<AiSettingsView>('/api/settings/ai', { method: 'PUT', body, signal });
  },

  async listKnowledge(signal?: AbortSignal): Promise<KnowledgeDocumentView[]> {
    return asList<KnowledgeDocumentView>(
      await request<unknown>('/api/settings/knowledge', { signal }),
    );
  },

  createKnowledge(
    body: CreateKnowledgeDocumentRequest,
    signal?: AbortSignal,
  ): Promise<KnowledgeDocumentView> {
    return request<KnowledgeDocumentView>('/api/settings/knowledge', {
      method: 'POST',
      body,
      signal,
    });
  },

  deleteKnowledge(id: string, signal?: AbortSignal): Promise<void> {
    return request<void>(`/api/settings/knowledge/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      signal,
    });
  },

  // --- Claves de API para integraciones --------------------------------------

  /** Solo claves activas: las revocadas no se devuelven. */
  async listApiKeys(signal?: AbortSignal): Promise<ApiKeyView[]> {
    return asList<ApiKeyView>(await request<unknown>('/api/settings/api-keys', { signal }));
  },

  /** La clave en claro (`apiKey`) solo viaja en esta respuesta: no se puede recuperar. */
  createApiKey(body: CreateApiKeyRequest, signal?: AbortSignal): Promise<CreateApiKeyResponse> {
    return request<CreateApiKeyResponse>('/api/settings/api-keys', {
      method: 'POST',
      body,
      signal,
    });
  },

  revokeApiKey(id: string, signal?: AbortSignal): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>(`/api/settings/api-keys/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      signal,
    });
  },

  // --- Plantillas de WhatsApp ------------------------------------------------

  /**
   * Plantillas de la organizacion activa, con la copia local del estado que
   * tienen en Meta. `sync: true` obliga a consultar a Meta antes de responder.
   *
   * Fuera de la ventana de 24 h solo se pueden enviar las `APPROVED`, asi que el
   * compositor filtra por ese estado.
   */
  async listTemplates(
    query: TemplateListQuery = {},
    signal?: AbortSignal,
  ): Promise<TemplateView[]> {
    const payload = await request<unknown>('/api/templates', {
      query: {
        accountId: query.accountId,
        status: query.status,
        // El backend coacciona con `Boolean()`, de modo que "false" seria
        // `true`: el parametro solo viaja cuando se pide sincronizar de verdad.
        sync: query.sync ? true : undefined,
      },
      signal,
    });
    return asList<TemplateView>(payload);
  },

  /**
   * Trae las plantillas de Meta y actualiza la copia local.
   * Responde 502 `meta_error` (con el motivo en `message`) si Meta rechaza la
   * consulta.
   */
  syncTemplates(
    body: { accountId?: string } = {},
    signal?: AbortSignal,
  ): Promise<TemplateSyncResponse> {
    return request<TemplateSyncResponse>('/api/templates/sync', {
      method: 'POST',
      body,
      signal,
    });
  },

  /**
   * Crea una plantilla en Meta (HTTP 201). Queda en revision hasta que Meta la
   * apruebe, asi que todavia no se puede enviar.
   *
   * La API responde 400 `invalid_body` con el detalle de zod en `details`, 400
   * `invalid_template` con los mensajes por campo que devuelve `revisarComponentes`
   * (ver `ApiError.invalidFields`) y 502 `meta_error` si Meta la rechaza.
   */
  createTemplate(body: CreateTemplateRequest, signal?: AbortSignal): Promise<TemplateView> {
    return request<TemplateView>('/api/templates', { method: 'POST', body, signal });
  },

  /** Borra la plantilla en Meta y la marca como borrada en la copia local. */
  deleteTemplate(id: string, signal?: AbortSignal): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/api/templates/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      signal,
    });
  },

  health(signal?: AbortSignal): Promise<HealthResponse> {
    return request<HealthResponse>('/health', { signal });
  },
};

/**
 * Nombre de fichero que propone la API en `Content-Disposition`.
 *
 * El backend lo manda como `attachment; filename="contactos-2025-01-01.csv"`,
 * pero se acepta tambien la forma `filename*=UTF-8''...` por si cambia.
 */
function csvFileName(header: string | null, fallback = 'contactos.csv'): string {
  if (!header) return fallback;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]).trim() || fallback;
    } catch {
      // Si el nombre no se puede decodificar se usa el de reserva.
    }
  }
  const simple = /filename="?([^";]+)"?/i.exec(header);
  return simple?.[1]?.trim() || fallback;
}

/**
 * Descarga los contactos en CSV (`GET /api/contacts/export`).
 *
 * La respuesta no es JSON, asi que no pasa por `request()`: se hace un `fetch`
 * aparte con las mismas cabeceras de autenticacion, se comprueba el estado y,
 * si todo va bien, se dispara la descarga con un `Blob` y un enlace temporal.
 * Un fallo se traduce a `ApiError` para que la pantalla lo muestre sin `alert`.
 */
export async function exportContactsCsv(
  query: { q?: string } = {},
  signal?: AbortSignal,
): Promise<void> {
  const headers: Record<string, string> = { Accept: 'text/csv' };
  if (currentAuth) {
    headers.Authorization = `Bearer ${currentAuth.token}`;
    headers['X-Tenant-Id'] = currentAuth.tenantId;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl('/api/contacts/export', { q: query.q }), {
      headers,
      signal,
    });
  } catch (error) {
    if (isAbortError(error)) throw error;
    throw new ApiError(
      0,
      'No se pudo conectar con el servidor. Comprueba que la API esta en marcha en http://localhost:3000.',
      'network_error',
      false,
    );
  }

  if (response.status === 401) {
    unauthorizedHandler?.();
    throw new ApiError(401, unauthorizedMessage(), 'unauthorized', false);
  }

  if (!response.ok) {
    const raw = await response.text();
    throw toApiError(response.status, raw.length > 0 ? parseJson(raw) : null);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = csvFileName(response.headers.get('content-disposition'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // El navegador ya se ha quedado con el contenido: se libera el objeto URL.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * `EventSource` no permite enviar cabeceras, asi que el token y la organizacion
 * activa viajan en la query string de `/api/events`.
 */
export function eventsUrl(token: string, tenantId?: string): string {
  const params = new URLSearchParams({ token });
  if (tenantId) params.set('tenantId', tenantId);
  return `/api/events?${params.toString()}`;
}

/**
 * URL autenticada del fichero multimedia de un mensaje.
 *
 * El token y la organizacion van en la query string, igual que en el canal de
 * eventos: un `<audio src>`, un `<img src>` o un enlace de descarga no permiten
 * enviar cabeceras. Sin sesion no hay URL: el panel siempre pinta mensajes con
 * sesion iniciada.
 */
export function mediaUrl(messageId: string, filename: string): string | null {
  if (!currentAuth) return null;
  const params = new URLSearchParams({ token: currentAuth.token, tenantId: currentAuth.tenantId });
  return `/api/media/${encodeURIComponent(messageId)}/${encodeURIComponent(filename)}?${params.toString()}`;
}

/** Error de envio que el panel muestra tal cual (incluye `requiresTemplate`). */
export type { SendMessageError };
